import { AndroidFiles, isAndroidApp } from './android-files.js'
import { installKeyboardTapRescue } from './keyboard-tap.js'
import { isNativeApp } from './native-session.js'

/**
 * What the installed app adds to the page once, at start (iPhone app sweep, 19 Oct 2026):
 *
 * - The keyboard: `@capacitor/keyboard` (configured in `apps/native/capacitor.config.ts`) shrinks
 *   the web view above the keyboard instead of letting WebKit scroll the whole page under the status
 *   bar, so fixed bars and bottom sheets stay put. The first tap on a button while the keyboard is
 *   up is also guaranteed to land (`keyboard-tap.ts`).
 * - Text size: the device's text size setting (Dynamic Type on iOS, font scale on Android) sizes
 *   the app's text, up to the 200% the layouts are built and tested for.
 *
 * A browser runs none of it.
 */

/** The smallest and largest text scale the layouts take (the web is checked at 200%). */
export const TEXT_SCALE_MIN = 0.85
export const TEXT_SCALE_MAX = 2

/** The device's preferred text scale, kept to what the layouts take, in 5% steps. */
export function appTextScale(preferred: number): number {
  if (!Number.isFinite(preferred) || preferred <= 0) return 1
  const clamped = Math.min(TEXT_SCALE_MAX, Math.max(TEXT_SCALE_MIN, preferred))
  return Math.round(clamped * 20) / 20
}

/**
 * The root font size for a scale. Every type token is in rem, so this scales all text — the same
 * lever the 200% check in `tests/e2e/specs/a11y.spec.ts` pulls. 100% leaves the browser default.
 */
export function rootFontSize(scale: number): string {
  return scale === 1 ? '' : `${Math.round(scale * 100)}%`
}

interface CapacitorPlatform {
  readonly getPlatform?: () => string
}

function nativePlatform(): string {
  const capacitor = (globalThis as { Capacitor?: CapacitorPlatform }).Capacitor
  return capacitor?.getPlatform?.() ?? 'web'
}

let started = false

/** Starts the installed app's page behaviour; a browser (or a second call) does nothing. */
export async function startNativeShell(): Promise<void> {
  if (started || !isNativeApp()) return
  started = true
  installKeyboardTapRescue(document)
  // The keyboard plugin's window events: bottom bars step aside while it is up (styles.css).
  const root = document.documentElement
  window.addEventListener('keyboardWillShow', () => {
    root.dataset.keyboard = 'open'
  })
  window.addEventListener('keyboardWillHide', () => {
    delete root.dataset.keyboard
  })
  await applyPreferredTextSize()
  try {
    const { App } = await import('@capacitor/app')
    // The person may change the text size in Settings while the app is in the background.
    await App.addListener('resume', () => void applyPreferredTextSize())
    if (isAndroidApp()) {
      await App.addListener('backButton', ({ canGoBack }) => {
        if (dismissNativeModal(document)) return
        if (window.location.pathname === '/' || !canGoBack) void App.exitApp()
        else window.history.back()
      })
    }
    const { SystemBars, SystemBarsStyle } = await import('@capacitor/core')
    const updateBars = (): void => {
      const style = getComputedStyle(root)
      const dark =
        root.dataset.theme === 'dark' ||
        (root.dataset.theme !== 'light' &&
          root.dataset.theme !== 'dark' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      void SystemBars.setStyle({
        style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
      }).catch(() => undefined)
      if (isAndroidApp()) {
        void AndroidFiles.setSystemBars({
          dark,
          background: style.getPropertyValue('--op-color-surface').trim(),
        }).catch(() => undefined)
      }
    }
    new MutationObserver(updateBars).observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateBars)
    await App.addListener('resume', updateBars)
    updateBars()
  } catch {
    // Without the App plugin the size is read again at the next launch.
  }
}

async function applyPreferredTextSize(): Promise<void> {
  try {
    const { TextZoom } = await import('@capacitor/text-zoom')
    const scale = appTextScale((await TextZoom.getPreferred()).value)
    if (nativePlatform() === 'android') {
      // Text-only WebView zoom enlarges glyphs without their rem-sized controls. Keep that at
      // 100% and scale the root instead, exactly as the large-text layout checks do.
      const current = (await TextZoom.get()).value
      if (Math.abs(current - 1) > 0.01) await TextZoom.set({ value: 1 })
    }
    document.documentElement.style.fontSize = rootFontSize(scale)
  } catch {
    // An older shell without the plugin keeps the default size.
  }
}

/** Tests only. */
export function resetNativeShellForTests(): void {
  started = false
}

/** Android Back belongs to the topmost open sheet before it belongs to page history. */
export function dismissNativeModal(document: Document): boolean {
  const dialogs = [...document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')]
  const dialog = dialogs.filter((entry) => entry.getClientRects().length > 0).at(-1)
  if (dialog === undefined) {
    // The editor's phone inspector/checklist are panels rather than aria-modal dialogs.
    const close = [...document.querySelectorAll<HTMLElement>('[data-native-back-close]')]
      .filter((entry) => entry.getClientRects().length > 0)
      .at(-1)
    if (close === undefined) return false
    close.click()
    return true
  }
  dialog.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
  )
  return true
}
